/**
 * VisitStart session document CRUD.
 *
 * A session lives in one of two places, and which one it is says who the intake
 * belongs to:
 *
 *   locations/{locationId}/visitStartSessions/{sessionId}
 *     Clinic-initiated. Someone scanned the clinic's poster. The clinic can list
 *     and read these — that is its check-in inbox — and the patient can still
 *     hand the same session to a clinician with the 7-digit code. One record,
 *     two readers.
 *
 *   visitStartSessions/{sessionId}
 *     Patient-initiated, no clinic (the bare /visitstart route and the patient
 *     dashboard mount). Nobody but the patient can read it, and the only way it
 *     reaches a clinician is the code. Unchanged from before the inbox existed.
 *
 * Everything here takes a session REF — `{ sessionId, locationId }` — rather than
 * a bare id, because a bare id no longer identifies a document. `locationId: null`
 * is the top-level case and has to be said explicitly: see visitStartSessionRef.
 *
 * Tier 1 scope: create / patch / read one session doc. The original had ~20 more
 * exports wrapping callables for staff-proxy consent, photo ID, SHA eligibility,
 * payment, QR invites, registration-desk staging and EHR transfer. None of those
 * exist in Tier 1 — re-add them with their Cloud Functions when you port the
 * matching increment.
 */

import firebase from "firebase/compat/app";
import PropTypes from "prop-types";
import { db } from "services/firebase";

export const VISIT_START_TCI_ID = "visit-start";
export const VISIT_START_SESSION_COLLECTION = "visitStartSessions";

/**
 * The `session` prop every element receives and hands to the answer writers.
 * Declared once here, beside visitStartSessionRef, because the two have to agree:
 * this is the shape that function accepts.
 *
 * `locationId` is nullable, not optional — a session with no clinic says so.
 */
export const VISIT_START_SESSION_PROP = PropTypes.shape({
  sessionId: PropTypes.string,
  locationId: PropTypes.string,
});

export function visitStartServerTimestamp() {
  return firebase.firestore.FieldValue.serverTimestamp();
}

/**
 * The one place a session's document path is decided.
 *
 * THROWS on a bare string, deliberately. Before the clinic inbox existed every
 * caller passed `sessionId` alone and there was exactly one collection it could
 * mean. Now there are two, and a caller that still passes a bare id is a caller
 * that has not been told which — so the failure has to be loud. Silently reading
 * it as "top-level" would send a poster session's answers to a document the
 * rules deny, and the fire-and-forget `issue*` writers only console.warn, so the
 * patient would answer a question and lose it with nothing on screen.
 *
 * @param {object} ref
 * @param {string} ref.sessionId
 * @param {?string} ref.locationId  `locations` doc id, or null for a session
 *                                  with no clinic. Must be stated.
 */
export function visitStartSessionRef(ref) {
  if (typeof ref === "string" || !ref || typeof ref !== "object") {
    throw new Error(
      "visitStartSessionRef needs { sessionId, locationId } — a bare session id " +
        "no longer identifies a document. Pass locationId: null for a session " +
        "with no clinic.",
    );
  }
  const { sessionId, locationId = null } = ref;
  if (!sessionId) throw new Error("sessionId is required.");
  if (!locationId) {
    return db.collection(VISIT_START_SESSION_COLLECTION).doc(sessionId);
  }
  return db
    .collection("locations")
    .doc(String(locationId))
    .collection(VISIT_START_SESSION_COLLECTION)
    .doc(sessionId);
}

/**
 * Firestore auto-id, generated client-side so the session id is known before
 * the document is written.
 *
 * Taken from the top-level collection whatever the session's eventual home is —
 * an auto-id is just a random id, and the rules only require that it match the
 * doc's own `sessionId` field.
 */
export function createVisitStartSessionId() {
  return db.collection(VISIT_START_SESSION_COLLECTION).doc().id;
}

/**
 * Create the session document. Called once at boot, before the element loop.
 *
 * `locationId` replaces the source project's `institutionId` (a level-3
 * hierarchyNodes id — a concept this app does not have). It is a `locations`
 * doc id, and it is OPTIONAL: a session with no clinic is valid and lives in the
 * top-level collection.
 *
 * It now decides two things rather than one. It is still recorded on the document
 * (so a reader holding the doc alone knows which clinic it belongs to), and it
 * also picks the document's PATH — which is what grants the clinic access, since
 * the rules gate the subcollection on membership of the location in the path. So
 * the caller must only pass a locationId it has confirmed resolves; the shell
 * checks that before calling (`clinic.valid`), because an id nobody can read
 * would file the intake under a clinic that cannot see it.
 *
 * @param {object}   args
 * @param {string}   args.sessionId
 * @param {string}   [args.locationId]     `locations` doc id, when the visit has one
 * @param {string}   [args.visitId]        `locations/{locationId}/visits` doc id,
 *                                         when intake follows a check-in
 * @param {string[]} args.enabledElements  resolved element order for this session
 * @param {string}   args.patientUid       the answering patient's uid
 * @param {string}   [args.status]         initial status. The shell creates the doc
 *                                         already in "element_loop" so starting is
 *                                         one write rather than a create then a patch.
 * @param {string}   [args.currentElementId] first element, for the same reason
 * @param {boolean}  [args.guest]          true when patientUid is an anonymous uid
 * @param {Date}     [args.guestExpiresAt] when a guest session self-deletes
 * @param {boolean}  [args.cmrShared]      patient chose to bring their record in
 */
export async function createVisitStartSession({
  sessionId,
  locationId = null,
  visitId = null,
  enabledElements = [],
  patientUid,
  status = "boot",
  currentElementId = null,
  guest = false,
  guestExpiresAt = null,
  cmrShared = false,
} = {}) {
  if (!sessionId || !patientUid) {
    throw new Error("sessionId and patientUid are required.");
  }

  const now = firebase.firestore.FieldValue.serverTimestamp();
  const payload = {
    sessionId,
    tciId: VISIT_START_TCI_ID,
    locationId: locationId || null,
    visitId: visitId || null,
    status,
    enabledElements: Array.isArray(enabledElements) ? enabledElements : [],
    currentElementId,
    sessionActor: {
      type: "patient",
      patientUid,
      staffUid: null,
    },
    createdAt: now,
    updatedAt: now,
    // The one consent decision the opening screen takes. Recorded here because it
    // also governs what leaves with the code the patient shows their clinician, and
    // that must not be re-decidable once answering has begun — like the two fields
    // below, it is absent from the rules' update allowlist.
    cmrShared: Boolean(cmrShared),
    // Guest sessions belong to an anonymous uid nobody can sign back into, so they
    // expire on their own. Both fields are write-once: the rules' create branch is
    // hasAll (extra keys pass) but the update branch is hasOnly, and neither key is
    // on it — so a client cannot extend its own expiry. Clearing them is therefore
    // the admin SDK's job, and the `claimVisitStartSession` callable is the one
    // caller: it runs when a guest creates an account on the completion screen and
    // the session stops being unowned. See visitStartAccountUpgrade.
    ...(guest
      ? {
          guest: true,
          ...(guestExpiresAt ? { guestExpiresAt } : {}),
        }
      : {}),
  };

  await visitStartSessionRef({ sessionId, locationId }).set(payload);
  return payload;
}

/**
 * Patch the session. Uses update() (not set({merge:true})) so callers can pass
 * dotted field paths like `elementAnswers.allergies` and write into the nested
 * map without replacing its siblings.
 *
 * @param {object} ref  { sessionId, locationId } — see visitStartSessionRef
 */
export async function updateVisitStartSession(ref, patch = {}) {
  await visitStartSessionRef(ref).update({
    ...patch,
    updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
  });
}

/**
 * @param {object} ref  { sessionId, locationId } — see visitStartSessionRef
 */
export async function getVisitStartSession(ref) {
  if (!ref?.sessionId) return null;
  const snap = await visitStartSessionRef(ref).get();
  if (!snap.exists) return null;
  return { id: snap.id, ...snap.data() };
}
